import React, { useEffect, useState } from 'react';
import { Target, TrendingUp, Check, Pencil } from 'lucide-react';
import {
  getLastNDays, getTodayWords, getDailyGoal, setDailyGoal, loadStatsFromServer,
  getWritingStreak, getPeriodStats,
} from '../../../utils/writingStats';

function formatCount(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return n.toLocaleString();
}

export function HomeStats() {
  const [todayWords, setTodayWords] = useState(getTodayWords());
  const [range, setRange] = useState<7 | 30>(7);
  const [days, setDays] = useState(() => getLastNDays(range));
  const [goal, setGoalState] = useState(getDailyGoal());
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');

  useEffect(() => {
    loadStatsFromServer().then(() => {
      setTodayWords(getTodayWords());
      setDays(getLastNDays(range));
      setGoalState(getDailyGoal());
    });
  }, [range]);

  const refresh = () => {
    setTodayWords(getTodayWords());
    setDays(getLastNDays(range));
    setGoalState(getDailyGoal());
  };

  const handleSaveGoal = () => {
    const value = Number(goalInput.trim());
    if (Number.isFinite(value) && value > 0) {
      setDailyGoal(value);
    } else {
      setDailyGoal(0);
    }
    setEditingGoal(false);
    refresh();
  };

  const startEditGoal = () => {
    setGoalInput(String(getDailyGoal() || ''));
    setEditingGoal(true);
  };

  const maxDay = Math.max(...days.map(d => d.words), 1);
  const goalPct = goal > 0 ? Math.min(100, Math.round((todayWords / goal) * 100)) : 0;
  const goalDone = goal > 0 && todayWords >= goal;
  const streak = getWritingStreak();
  const period = getPeriodStats(range, goal);

  return (
    <div className="home-stats">
      <div className="home-stats-left">
        <div className="home-stats-today">
          <span className="home-stats-label">今日写作</span>
          <span className="home-stats-value">{formatCount(todayWords)}<small> 字</small></span>
        </div>
        <div className="home-stats-goal">
          <div className="home-stats-goal-head">
            <span className="home-stats-label">
              <Target size={13} />
              每日目标 {goal > 0 ? `· ${formatCount(goal)} 字` : '（未设置）'}
            </span>
            {editingGoal ? (
              <div className="home-stats-goal-edit">
                <input
                  type="number"
                  min="0"
                  value={goalInput}
                  onChange={e => setGoalInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveGoal(); if (e.key === 'Escape') setEditingGoal(false); }}
                  placeholder="字数"
                  autoFocus
                />
                <button onClick={handleSaveGoal} title="保存目标"><Check size={13} /></button>
              </div>
            ) : (
              <button className="home-stats-goal-edit-btn" onClick={startEditGoal} title="设置每日目标">
                <Pencil size={12} />
              </button>
            )}
          </div>
          {goal > 0 && (
            <div className="home-stats-progress">
              <div className="home-stats-progress-bar">
                <div
                  className={`home-stats-progress-fill${goalDone ? ' is-done' : ''}`}
                  style={{ width: `${goalPct}%` }}
                />
              </div>
              <span className="home-stats-progress-pct">
                {goalDone ? '目标达成！' : `${goalPct}%`}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="home-stats-chart">
        <div className="home-stats-chart-head">
          <span className="home-stats-label"><TrendingUp size={13} /> 近 {range} 天</span>
          <div className="home-stats-range-toggle">
            {([7, 30] as const).map((r) => (
              <button
                key={r}
                className={`home-stats-range-btn${range === r ? ' is-active' : ''}`}
                onClick={() => setRange(r)}
              >
                {r}天
              </button>
            ))}
          </div>
        </div>
        <div className="home-stats-metrics">
          <div className="home-stats-metric" title="连续写作天数（今天未写则从昨天起算）">
            <span className="home-stats-metric-value">{streak}</span>
            <span className="home-stats-metric-label">连续天数</span>
          </div>
          <div className="home-stats-metric">
            <span className="home-stats-metric-value">{formatCount(period.total)}</span>
            <span className="home-stats-metric-label">{range}天总字数</span>
          </div>
          <div className="home-stats-metric">
            <span className="home-stats-metric-value">{formatCount(period.avg)}</span>
            <span className="home-stats-metric-label">平均/天</span>
          </div>
          <div className="home-stats-metric">
            <span className="home-stats-metric-value">{period.goalDays}</span>
            <span className="home-stats-metric-label">达标天数</span>
          </div>
        </div>
        <div className="home-stats-bars">
          {days.map(d => (
            <div key={d.date} className="home-stats-bar-col" title={`${d.label}：${d.words.toLocaleString()} 字`}>
              <div className="home-stats-bar-track">
                <div
                  className={`home-stats-bar-fill${d.words > 0 ? ' has-words' : ''}${d.date === days[days.length - 1].date ? ' is-today' : ''}`}
                  style={{ height: `${Math.max(4, Math.round((d.words / maxDay) * 100))}%` }}
                />
              </div>
              <span className="home-stats-bar-label">{d.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
